// Sugalarm — choose a CGM provider before sign-in.

import { Logo } from "../../components/ui/Logo";
import type { Provider } from "./providers";
import { PROVIDERS } from "./providers";

interface Props {
  onPick: (p: Provider) => void;
  onSkip?: () => void;
  /** Cycle 331: show a tappable "pair this device" card below the
   *  provider grid — lets someone setting up a device pair it to a
   *  household already signed in elsewhere instead of connecting a
   *  fresh CGM here. Default false, and both this AND `onPairDevice`
   *  must be set for the card to render, so every existing call site
   *  (the Settings CgmConnectModal, the per-member Members-pane picker)
   *  renders byte-identical to before. Only the onboarding "Connect your
   *  CGM" call site (LoginScreen) passes it. */
  showPairDevice?: boolean;
  onPairDevice?: () => void;
}

export function ProviderPicker({
  onPick,
  onSkip,
  showPairDevice = false,
  onPairDevice,
}: Props) {
  return (
    <div style={{ position: "relative", maxWidth: 760, width: "100%" }}>
      <h1
        style={{
          margin: 0,
          fontSize: 28,
          fontWeight: 600,
          letterSpacing: "-0.025em",
          textAlign: "center",
          lineHeight: 1.1,
        }}
      >
        Connect your CGM
      </h1>
      <p
        style={{
          margin: "8px 0 24px",
          fontSize: 14,
          color: "var(--ink-2)",
          textAlign: "center",
        }}
      >
        Choose your continuous glucose monitor to begin pairing.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        {PROVIDERS.map((p) => (
          <button
            key={p.id}
            onClick={() => onPick(p)}
            style={{
              appearance: "none",
              textAlign: "left",
              background: "var(--bg-elev)",
              border: "0.5px solid var(--line-strong)",
              borderRadius: 16,
              padding: "20px 22px",
              display: "flex",
              flexDirection: "column",
              gap: 12,
              color: "var(--ink)",
              minHeight: 180,
              cursor: "pointer",
              transition: "transform 120ms, border-color 120ms, background 120ms",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.borderColor = p.color;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.borderColor = "var(--line-strong)";
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 14,
                background: `${p.color}1A`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {p.glyph(32)}
            </div>
            <div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  letterSpacing: "-0.015em",
                }}
              >
                {p.name}
              </div>
              <div
                style={{
                  fontSize: "var(--type-floor)",
                  fontWeight: 600,
                  color: "var(--ink-3)",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  marginTop: 2,
                }}
              >
                {p.models}
              </div>
            </div>
            <div
              style={{
                fontSize: "var(--type-floor)",
                color: "var(--ink-2)",
                lineHeight: 1.45,
                flex: 1,
              }}
            >
              {p.blurb}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginTop: 4,
              }}
            >
              <span
                style={{
                  fontSize: "var(--type-floor)",
                  fontWeight: 600,
                  color: p.color,
                }}
              >
                Choose →
              </span>
              <span
                style={{
                  fontSize: "var(--type-floor)",
                  fontWeight: 600,
                  color: "var(--ink-3)",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                Pair
              </span>
            </div>
          </button>
        ))}
      </div>
      {showPairDevice && onPairDevice && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              margin: "22px 0",
            }}
          >
            <div style={{ flex: 1, height: 1, background: "var(--line-strong)" }} />
            <span
              style={{
                fontSize: "var(--type-floor)",
                fontWeight: 700,
                letterSpacing: "0.08em",
                color: "var(--ink-3)",
              }}
            >
              OR
            </span>
            <div style={{ flex: 1, height: 1, background: "var(--line-strong)" }} />
          </div>

          <button
            type="button"
            onClick={onPairDevice}
            style={{
              appearance: "none",
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 14,
              textAlign: "left",
              background: "color-mix(in srgb, var(--accent) 10%, var(--bg-elev))",
              border: "1px solid var(--accent)",
              borderRadius: 16,
              padding: "16px 18px",
              minHeight: 44,
              cursor: "pointer",
              color: "var(--ink)",
            }}
          >
            <Logo size={40} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  letterSpacing: "-0.01em",
                }}
              >
                Are you logged into the app?
              </div>
              <div
                style={{
                  fontSize: "var(--type-floor)",
                  color: "var(--ink-2)",
                  marginTop: 2,
                  lineHeight: 1.4,
                }}
              >
                Tap here to pair this device to a household — no CGM setup
                needed on this screen.
              </div>
            </div>
            <span
              aria-hidden
              style={{ fontSize: 22, color: "var(--accent)", flexShrink: 0 }}
            >
              ›
            </span>
          </button>
        </>
      )}
      {onSkip && (
        <div
          style={{
            marginTop: 18,
            textAlign: "center",
            fontSize: "var(--type-floor)",
            color: "var(--ink-3)",
          }}
        >
          Skip for now —{" "}
          <button
            type="button"
            onClick={onSkip}
            style={{
              appearance: "none",
              border: 0,
              background: "transparent",
              color: "var(--accent)",
              fontWeight: 600,
              cursor: "pointer",
              textDecoration: "underline",
              textUnderlineOffset: 3,
              textDecorationStyle: "dotted",
            }}
          >
            connect later
          </button>
        </div>
      )}
    </div>
  );
}
